import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa6';
import { useAppContext } from '../../../../shared/context/AppContext';
import Button from '../../../../shared/components/Button';
import ResponsibilityDropdown from '../components/ResponsibilityDropdown';
import { getAllResponsibilities, createResponsibility, getResponsibilitiesByRole, getUniversalResponsibilities } from '../../data/services/responsibilityService';
import * as promptLibraryService from '../../../promptLibrary/data/services/promptLibraryService';
import { updateResponsibilities, updateFrameworks, setUserRole, getUserRoleSetup } from '../../data/services/userRoleService';
import { getRolesForUser } from '../../data/services/roleService';
import { getUserId } from '../../../../shared/domain/services/userService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './EditRoleScreen.css';

// Sortable Responsibility Item Component
const SortableResponsibilityItem = ({ 
  id, 
  responsibility, 
  frameworks, 
  availablePrompts,
  frameworksByResponsibility,
  openFrameworkDropdown,
  frameworkSearchQuery,
  frameworkDropdownRef,
  onRemoveResponsibility,
  onRemoveFramework,
  onAddFramework,
  onToggleFrameworkDropdown,
  onFrameworkSearchChange,
  getFilteredPrompts
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="responsibility-row">
      <div className="responsibility-row-header">
        <div className="responsibility-row-content">
          <div 
            className="responsibility-drag-handle"
            {...attributes}
            {...listeners}
            role="button"
            tabIndex={0}
            aria-label="Drag to reorder"
          >
            <span className="drag-handle-icon">⋮⋮</span>
          </div>
          <h3 className="responsibility-row-title">{responsibility.responsibilityName}</h3>
        </div>
        <button
          className="responsibility-remove-button"
          onClick={() => onRemoveResponsibility(id)}
        >
          REMOVE
        </button>
      </div>
      
      <div className="prompts-container-wrapper" ref={openFrameworkDropdown === id ? frameworkDropdownRef : null}>
        <div className="prompts-container">
          <div className="prompts-row">
            {frameworks.map((framework, idx) => {
              const prompt = availablePrompts.find(p => p.id === framework.promptId);
              if (!prompt) return null;
              
              return (
                <div key={`${framework.promptId}-${idx}`} className="prompt-chip">
                  <span className="prompt-chip-text">{prompt.title || prompt.promptTitle || 'Untitled Prompt'}</span>
                  <button
                    className="prompt-chip-remove"
                    onClick={() => onRemoveFramework(id, framework.promptId, framework.isGlobalPrompt)}
                    aria-label={`Remove ${prompt.title}`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
          
          <div className="prompt-dropdown-wrapper">
            <button
              className="prompt-dropdown-button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFrameworkDropdown(id);
              }}
              type="button"
            >
              <span className="prompt-dropdown-arrow">▼</span>
            </button>
          </div>
        </div>
        
        {openFrameworkDropdown === id && (
          <div 
            className="prompt-dropdown-menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="prompt-dropdown-search">
              <span className="prompt-dropdown-search-icon">🔍</span>
              <input
                type="text"
                className="prompt-dropdown-search-input"
                placeholder="Search prompts..."
                value={frameworkSearchQuery}
                onChange={(e) => {
                  e.stopPropagation();
                  onFrameworkSearchChange(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
            <div className="prompt-dropdown-list">
              {(() => {
                const filtered = getFilteredPrompts();
                const currentFrameworks = frameworksByResponsibility[id] || [];
                
                if (filtered.length > 0) {
                  return filtered.map(prompt => {
                    const isAlreadyAdded = currentFrameworks.some(
                      f => f.promptId === prompt.id
                    );
                    
                    return (
                      <button
                        key={prompt.id}
                        className={`prompt-dropdown-item ${isAlreadyAdded ? 'disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (!isAlreadyAdded) {
                            onAddFramework(id, prompt);
                          }
                        }}
                        disabled={isAlreadyAdded}
                        type="button"
                      >
                        {prompt.title || prompt.promptTitle || 'Untitled Prompt'}
                        {isAlreadyAdded && <span className="prompt-added-badge">Added</span>}
                      </button>
                    );
                  });
                } else {
                  return (
                    <div className="prompt-dropdown-no-results">
                      {frameworkSearchQuery.trim() ? 'No prompts found' : 'No prompts available'}
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EditRoleScreen = () => {
  const navigate = useNavigate();
  const { user, organization, setUser } = useAppContext();
  const [availableResponsibilities, setAvailableResponsibilities] = useState([]);
  const [selectedResponsibilities, setSelectedResponsibilities] = useState([]); // Array of { responsibilityId, displayOrder }
  const [frameworksByResponsibility, setFrameworksByResponsibility] = useState({});
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [frameworkSearchQuery, setFrameworkSearchQuery] = useState('');
  const [openFrameworkDropdown, setOpenFrameworkDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const frameworkDropdownRef = useRef(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(user?.jobRole || '');
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [roleResponsibilities, setRoleResponsibilities] = useState([]);

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadAvailableResponsibilities = useCallback(async () => {
    try {
      const allResp = await getAllResponsibilities();
      setAvailableResponsibilities(allResp);
    } catch (error) {
      console.error('Error loading responsibilities:', error);
    }
  }, []);

  const loadAvailablePrompts = useCallback(async () => {
    try {
      const orgId = organization?.id || organization?.orgId;
      const userId = getUserId(user);
      const prompts = await promptLibraryService.getAllPrompts({
        organizationId: orgId || null,
        userId: userId || null,
        promptType: 'all'
      });
      setAvailablePrompts(prompts);
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  }, [organization, user]);

  const loadAvailableRoles = useCallback(async () => {
    try {
      const roles = await getRolesForUser();
      setAvailableRoles(roles);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  }, []);

  const loadRoleResponsibilities = useCallback(async (roleName) => {
    if (!roleName) {
      setRoleResponsibilities([]);
      return;
    }
    try {
      const role = availableRoles.find(r => r.roleName === roleName);
      if (role) {
        const [roleResp, universalResp] = await Promise.all([
          getResponsibilitiesByRole(role.roleId),
          getUniversalResponsibilities()
        ]);
        // Combine role-specific and universal responsibilities
        setRoleResponsibilities([...roleResp, ...universalResp]);
      }
    } catch (error) {
      console.error('Error loading role responsibilities:', error);
      setRoleResponsibilities([]);
    }
  }, [availableRoles]);

  useEffect(() => {
    loadAvailableRoles();
    loadAvailableResponsibilities();
    loadAvailablePrompts();
    setSelectedRole(user?.jobRole || '');
    
    // Load current role setup
    const loadCurrentSetup = async () => {
      try {
        const orgId = organization?.id || organization?.orgId;
        const roleSetup = await getUserRoleSetup(orgId || null);
        // Responsibilities are already ordered by displayOrder from the API
        // Map them to objects with responsibilityId and displayOrder
        const responsibilitiesWithOrder = roleSetup.responsibilities.map((r, index) => ({
          responsibilityId: r.responsibilityId,
          displayOrder: r.displayOrder !== undefined && r.displayOrder !== null ? r.displayOrder : index
        }));
        setSelectedResponsibilities(responsibilitiesWithOrder);
        
        const frameworksMap = {};
        responsibilitiesWithOrder.forEach(({ responsibilityId }) => {
          if (roleSetup.frameworks && roleSetup.frameworks[responsibilityId]) {
            frameworksMap[responsibilityId] = (roleSetup.frameworks[responsibilityId] || []).map(f => ({
              promptId: f.promptId || f.prompt?.id,
              isGlobalPrompt: f.isGlobalPrompt === true || f.isGlobalPrompt === 1 || f.isGlobalPrompt === '1'
            }));
          } else {
            frameworksMap[responsibilityId] = [];
          }
        });
        setFrameworksByResponsibility(frameworksMap);
      } catch (error) {
        console.error('Error loading current setup:', error);
      }
    };
    
    loadCurrentSetup();
  }, [user, organization, loadAvailableRoles, loadAvailableResponsibilities, loadAvailablePrompts]);

  useEffect(() => {
    if (selectedRole) {
      loadRoleResponsibilities(selectedRole);
    }
  }, [selectedRole, loadRoleResponsibilities]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (frameworkDropdownRef.current && !frameworkDropdownRef.current.contains(event.target)) {
        setOpenFrameworkDropdown(null);
        setFrameworkSearchQuery('');
      }
    };

    if (openFrameworkDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openFrameworkDropdown]);

  const handleRoleChange = async (newRole) => {
    if (!newRole || newRole === selectedRole) return;
    
    setIsChangingRole(true);
    try {
      const isManagement = user?.isManagement === true || user?.isManagement === 1 || user?.isManagement === '1';
      const response = await setUserRole(newRole, isManagement);
      
      if (response && response.user) {
        setUser(response.user);
        setSelectedRole(newRole);
        
        // Reload role setup to get new responsibilities and frameworks
        const orgId = organization?.id || organization?.orgId;
        const roleSetup = await getUserRoleSetup(orgId || null);
        
        // Update selected responsibilities with order
        const responsibilitiesWithOrder = roleSetup.responsibilities.map((r, index) => ({
          responsibilityId: r.responsibilityId,
          displayOrder: r.displayOrder !== undefined && r.displayOrder !== null ? r.displayOrder : index
        }));
        setSelectedResponsibilities(responsibilitiesWithOrder);
        
        // Update frameworks
        const frameworksMap = {};
        if (roleSetup.frameworks) {
          Object.keys(roleSetup.frameworks).forEach(respId => {
            frameworksMap[respId] = (roleSetup.frameworks[respId] || []).map(f => ({
              promptId: f.promptId || f.prompt?.id,
              isGlobalPrompt: f.isGlobalPrompt === true || f.isGlobalPrompt === 1 || f.isGlobalPrompt === '1'
            }));
          });
        }
        setFrameworksByResponsibility(frameworksMap);
        
        // Reload available responsibilities
        await loadAvailableResponsibilities();
      }
    } catch (error) {
      console.error('Error changing role:', error);
      alert('Failed to change role. Please try again.');
    } finally {
      setIsChangingRole(false);
    }
  };

  const handleRemoveResponsibility = (responsibilityId) => {
    setSelectedResponsibilities(prev => prev.filter(item => item.responsibilityId !== responsibilityId));
    const newFrameworks = { ...frameworksByResponsibility };
    delete newFrameworks[responsibilityId];
    setFrameworksByResponsibility(newFrameworks);
  };

  const handleAddResponsibility = (responsibilityId) => {
    const exists = selectedResponsibilities.some(item => item.responsibilityId === responsibilityId);
    if (!exists) {
      // Get max displayOrder and add 1, or use array length
      const maxOrder = selectedResponsibilities.length > 0
        ? Math.max(...selectedResponsibilities.map(item => item.displayOrder !== null && item.displayOrder !== undefined ? item.displayOrder : -1))
        : -1;
      const newOrder = maxOrder + 1;
      setSelectedResponsibilities(prev => [...prev, { responsibilityId, displayOrder: newOrder }]);
      setFrameworksByResponsibility(prev => ({
        ...prev,
        [responsibilityId]: []
      }));
    }
  };

  const handleCreateCustomResponsibility = async (responsibilityName) => {
    try {
      const newResponsibility = await createResponsibility(responsibilityName.trim());
      
      setAvailableResponsibilities(prev => [...prev, newResponsibility]);
      handleAddResponsibility(newResponsibility.responsibilityId);
    } catch (error) {
      console.error('Error creating custom responsibility:', error);
      alert('Failed to create custom responsibility. Please try again.');
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSelectedResponsibilities((items) => {
        const oldIndex = items.findIndex(item => item.responsibilityId === active.id);
        const newIndex = items.findIndex(item => item.responsibilityId === over.id);
        
        // Validate that both indices were found
        if (oldIndex === -1 || newIndex === -1) {
          console.warn('Drag operation failed: could not find responsibility in state', {
            activeId: active.id,
            overId: over.id,
            oldIndex,
            newIndex,
            itemsCount: items.length
          });
          return items; // Return unchanged state if indices are invalid
        }
        
        const reordered = arrayMove(items, oldIndex, newIndex);
        // Update displayOrder values based on new positions
        return reordered.map((item, index) => ({
          ...item,
          displayOrder: index
        }));
      });
    }
  };

  const handleRemoveFramework = (responsibilityId, promptId, isGlobalPrompt) => {
    setFrameworksByResponsibility(prev => ({
      ...prev,
      [responsibilityId]: (prev[responsibilityId] || []).filter(
        f => !(f.promptId === promptId && f.isGlobalPrompt === isGlobalPrompt)
      )
    }));
  };

  const handleAddFramework = (responsibilityId, prompt) => {
    const isGlobalPrompt = prompt.isGlobal || false;
    const framework = {
      promptId: prompt.id,
      isGlobalPrompt: isGlobalPrompt
    };
    
    setFrameworksByResponsibility(prev => {
      const currentFrameworks = prev[responsibilityId] || [];
      const exists = currentFrameworks.some(
        f => f.promptId === framework.promptId && f.isGlobalPrompt === framework.isGlobalPrompt
      );
      if (exists) {
        return prev;
      }
      return {
        ...prev,
        [responsibilityId]: [...currentFrameworks, framework]
      };
    });
    
    setOpenFrameworkDropdown(null);
    setFrameworkSearchQuery('');
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Send responsibilities with displayOrder
      const responsibilitiesWithOrder = selectedResponsibilities.map(item => ({
        responsibilityId: item.responsibilityId,
        displayOrder: item.displayOrder
      }));
      await updateResponsibilities(responsibilitiesWithOrder);
      
      for (const item of selectedResponsibilities) {
        const frameworks = frameworksByResponsibility[item.responsibilityId] || [];
        const promptIds = frameworks.map(f => f.promptId);
        const isGlobalPrompts = frameworks.map(f => f.isGlobalPrompt);
        await updateFrameworks(item.responsibilityId, promptIds, isGlobalPrompts);
      }
      
      navigate('/home');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save changes. Please try again.');
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/home');
  };

  const getFilteredPrompts = () => {
    if (!frameworkSearchQuery.trim()) {
      return availablePrompts;
    }
    const queryLower = frameworkSearchQuery.toLowerCase().trim();
    return availablePrompts.filter(prompt => {
      const title = (prompt.title || prompt.promptTitle || '').toLowerCase();
      const description = (prompt.description || '').toLowerCase();
      return title.includes(queryLower) || description.includes(queryLower);
    });
  };


  // Extract responsibility IDs for the sortable context
  const responsibilityIds = selectedResponsibilities.map(item => item.responsibilityId);

  return (
    <div className="edit-role-screen">
      <button
        className="edit-role-back-button"
        onClick={handleBackClick}
        aria-label="Back to Prompt Library"
      >
        <FaArrowLeft className="edit-role-back-icon" />
      </button>
      <div className="edit-role-main">
        <div className="prompt-details-card">
          <div className="prompt-details-gradient-header"></div>
          <div className="prompt-details-card-content">
            {/* Selected Role */}
            <div className="edit-role-section">
              <label className="form-label headline-medium">Your selected role</label>
              <select
                className="edit-role-select"
                value={selectedRole}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={isChangingRole}
              >
                <option value="">Select a role...</option>
                {availableRoles.map(role => (
                  <option key={role.roleId} value={role.roleName}>
                    {role.roleName}
                  </option>
                ))}
              </select>
              {isChangingRole && <div className="edit-role-loading-text">Loading role preset...</div>}
            </div>

            {/* Divider */}
            <div className="edit-role-divider"></div>

            {/* Responsibilities and Prompts */}
            <div className="edit-role-section">
              <label className="form-label headline-medium responsibilities-label">Responsibilities & Prompts</label>
              
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={responsibilityIds}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedResponsibilities.map(item => {
                    const resp = availableResponsibilities.find(r => r.responsibilityId === item.responsibilityId);
                    if (!resp) return null;
                    const frameworks = frameworksByResponsibility[item.responsibilityId] || [];
                    
                    return (
                      <SortableResponsibilityItem
                        key={item.responsibilityId}
                        id={item.responsibilityId}
                        responsibility={resp}
                        frameworks={frameworks}
                        availablePrompts={availablePrompts}
                        frameworksByResponsibility={frameworksByResponsibility}
                        openFrameworkDropdown={openFrameworkDropdown}
                        frameworkSearchQuery={frameworkSearchQuery}
                        frameworkDropdownRef={frameworkDropdownRef}
                        onRemoveResponsibility={handleRemoveResponsibility}
                        onRemoveFramework={handleRemoveFramework}
                        onAddFramework={handleAddFramework}
                        onToggleFrameworkDropdown={(respId) => {
                          const newOpenState = openFrameworkDropdown === respId ? null : respId;
                          setOpenFrameworkDropdown(newOpenState);
                          setFrameworkSearchQuery('');
                        }}
                        onFrameworkSearchChange={setFrameworkSearchQuery}
                        getFilteredPrompts={getFilteredPrompts}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            </div>

            {/* Add a responsibility */}
            <div className="edit-role-section">
              <label className="form-label headline-medium">Add a responsibility</label>
              <ResponsibilityDropdown
                responsibilities={roleResponsibilities}
                selectedResponsibilities={selectedResponsibilities.map(item => item.responsibilityId)}
                onSelect={handleAddResponsibility}
                onAddNew={handleCreateCustomResponsibility}
              />
            </div>

            {/* Save Button */}
            <div className="edit-role-actions">
              <Button
                type="button"
                variant="primary"
                onClick={handleSave}
                disabled={loading}
                className="edit-role-save-button"
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRoleScreen;
